/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type ComponentRef, type OnDestroy, type OnInit, Component, Input } from '@angular/core';
import {
    type Polling, type RestJob, EventService, HttpService, LazyloadService, LocalStorageService,
    PermissionService, PollingService, PromptService, UNPLACEHOLDER,
} from '@dm/core';
import { AuthService } from 'app/common/services';
import { ProductConfig } from 'app/config';
import { UpgradeService as MainUpgradeService } from 'app/feature/settings/basic-infomation/upgrade/upgrade.service';
import { isUndefined } from 'lodash';
import { type Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { DetailCheckResultUpgradeComponent } from '../detail-check-result-upgrade/detail-check-result-upgrade.component';
import {
    type PreCheckParams, type StatusCount, CheckResult, CheckStatusE, CmoQueryFrom, CmoQueryTypeE, CmoUpdSysModeE,
    CmoUpdSysStepTaskStatusE, DoneStatus, Progress, UpgradeModeE, UpgradeTypeE,
} from '../upgrade.const';
import { UpgradeService } from '../upgrade.service';
import { StatusIcon, UpgradeStrService } from '../upgrade-str.service';

@Component({
    selector: 'app-system-upgrade',
    templateUrl: './system-upgrade.component.html',
    styleUrls: ['./system-upgrade.component.scss'],
    host: {
        '[class.hidden]': '!iconVisible',
    },
})

export class SystemUpgradeComponent implements OnInit, OnDestroy {
    @Input() visible: boolean;
    @Input() iconVisible: boolean;

    i18n = this.str.i18n;
    sysUpProgressDesc = this.str.sysUpProgressDesc;

    // 检查详情轮询(预检查、验证)
    checkResultPolling: Polling;
    // 升级详情轮询
    upgradeProgressPolling: Polling;

    upgradePolling: Polling;
    // 当前升级状态 preCheck、upgrade、verificatng
    upgradeStatus = {
        checkProgress: 0,
        status: CmoUpdSysStepTaskStatusE.Upgrading,
    };
    _currentProcess: Progress;
    // 检查详情
    checkDetails = [];
    // 升级详情
    upgradeDetails: any;
    // 操作按钮状态
    btnEnabled = false;
    isCheck = false;
    UNPLACEHOLDER = UNPLACEHOLDER;

    // 进度条动画。切换步骤时进度会重置，此时不应该有动画
    progressMotion = true;

    upgradeEnable = false;

    Progress = Progress;

    CheckResult = CheckResult;

    // 是否执行过额外的轮询,用以保证每次检查额外轮询只执行一次
    extraPolling = false;

    sub$: Subscription;

    /**
     * 处在升级流程中
     */
    inUpgradeProcessSub$: Subscription;

    count: StatusCount = {
        all: 0,
        suc: 0,
        fail: 0,
        ignore: 0,
    };

    /**
     * 保存当前打开的详情页组件，用于数据双向沟通
     */
    detailComponent: ComponentRef<any>;

    /**
     * 当前打开的检查详情数据key
     *
     * 以检查详情的checkItem值作为key
     */
    checkDetailDataKey: string;

    /**
     * 升级面板保持不关闭。
     */
    noClosePanel = false;

    set currentProcess(value: Progress) {
        this._currentProcess = value;
        // 赋值的同时修改公共状态
        this.mainUpgradeService.upgradeProcess = value;
        this.mainUpgradeService.upgradeMode = UpgradeModeE.System;
    }

    get currentProcess(): Progress {
        return this._currentProcess;
    }

    constructor(
        private http: HttpService,
        private poll: PollingService,
        public str: UpgradeStrService,
        private upgradeService: UpgradeService,
        private lazyLoad: LazyloadService,
        private service: UpgradeService,
        private authService: AuthService,
        private localStorageService: LocalStorageService,
        private promptService: PromptService,
        private permissionService: PermissionService,
        private eventService: EventService,
        private mainUpgradeService: MainUpgradeService,
    ) { }

    ngOnInit(): void {
        this.isCheck = true;

        if (this.permissionService.isSuperAdmin() && this.permissionService.hasPermission('support_major_W')) {
            this.initDeviceInfo();
        }
    }

    initDeviceInfo(): void {
        this.sub$ = this.eventService.subscribe('systemInfoUpdate', this.getIsShowUpgrade);
        this.getIsShowUpgrade();
    }

    getIsShowUpgrade = (): void => {
        const ifShowUpgrade = this.upgradeService.ifShowUpgrade();
        if (ifShowUpgrade !== undefined) {
            this.sub$.unsubscribe();
            if (ifShowUpgrade && !ProductConfig.isHideUpgrade) {
                this.reenter();
            }
        }
    };

    /**
     * 重入处理
     */
    reenter(): void {
        this.inUpgradeProcessSub$ = this.eventService.subscribe('inUpgradeProcess',
            (data: { type: CmoUpdSysModeE; process: CmoUpdSysStepTaskStatusE }) => {
                if (data.type === CmoUpdSysModeE.FastUpgrade) {
                    // 正在升级
                    if (Number(data.process) === CmoUpdSysStepTaskStatusE.Upgrading) {
                        this.isCheck = false;
                        this.currentProcess = Progress.upgrading;
                        this.upgradeStatus.checkProgress = 0;
                        this.btnEnabled = false;
                        this.checkDetails = [];
                        this.show();
                        // 开始升级中轮询
                        this.createUpgradePolling();
                        // 正在回滚
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.Rollbacking) {
                        this.isCheck = false;
                        this.currentProcess = Progress.rollBacking;
                        this.upgradeStatus.checkProgress = 0;
                        this.btnEnabled = false;
                        this.checkDetails = [];
                        this.show();
                        // 开始回滚中轮询
                        this.createUpgradePolling();
                        // 升级暂停(包含升级失败、升级暂停)
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.UpgradePause) {
                        this.isCheck = false;
                        this.currentProcess = Progress.upgradePause;
                        this.btnEnabled = true;
                        this.checkDetails = [];
                        this.createUpgradePolling();
                        // 回滚暂停(包含回滚失败、回滚暂停)
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.RollbackFail ||
                        Number(data.process) === CmoUpdSysStepTaskStatusE.RollbackPause) {
                        this.isCheck = false;
                        this.currentProcess = Progress.rollBackFailed;
                        this.btnEnabled = true;
                        this.checkDetails = [];
                        this.createUpgradePolling();
                        // 升级成功且有升级标志
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.UpgradeSuc &&
                        this.localStorageService.get('systemUpgrade')) {
                        this.promptService.success({
                            title: this.i18n.eventLevelInfo,
                            content: this.i18n.sysUpgradePromptSuc,
                            onOk: this.promptService.close,
                        });
                        // 回滚成功且有升级标志
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.RollbackSuc &&
                        this.localStorageService.get('systemUpgrade')) {
                        this.promptService.success({
                            title: this.i18n.eventLevelInfo,
                            content: this.i18n.rollBackSuc,
                            onOk: this.promptService.close,
                        });
                        // 升级失败且有升级标志
                    } else if (Number(data.process) === CmoUpdSysStepTaskStatusE.UpgradeFail &&
                        this.localStorageService.get('systemUpgrade')) {
                        this.promptService.fail({
                            title: this.i18n.eventLevelInfo,
                            content: this.i18n.sysUpgradeFail,
                            onOk: this.promptService.close,
                        });
                    }
                }
                // 清除升级标志
                this.localStorageService.remove('systemUpgrade');
            });
    }

    show(): void {
        this.iconVisible = true;
        this.visible = true;
        // 进入升级流程
        this.mainUpgradeService.inUpgradeProcess = true;
    }

    updateProgressMotion(): void {
        this.progressMotion = false;
        setTimeout(() => {
            this.progressMotion = true;
        });
    }

    // 检查结果计数
    getCount(): StatusCount {
        let all = 0;
        let suc = 0;
        let fail = 0;
        let ignore = 0;
        all = this.checkDetails.length;
        this.checkDetails.forEach((items) => {
            switch (Number(items.checkResult)) {
                case CheckResult.Suc: suc++; break;
                case CheckResult.Fail: fail++; break;
                case CheckResult.Error: fail++; break;
                case CheckResult.Ignore: ignore++; break;
                default: break;
            }
        });
        return { all, suc, fail, ignore };
    }

    // 获取失败计数和能否上传状态
    getStatus(): void {
        this.count = this.getCount();
    }

    getSkipItemList(): string {
        const list: number[] = [];
        this.checkDetails.forEach((item) => {
            if (Number(item.checkResult) === CheckResult.Ignore) {
                const items = item.checkItem.split(':');
                if (items.length === 2) {
                    list.push(items[0]);
                }
            }
        });
        return list.join(',');
    }

    /**
     *  升级前检查
     * @param retry 重试模式
     */
    precheck(retry = false): Promise<void> {
        // 1重试 0开始
        const checkMode = retry ? 1 : 0;
        this.currentProcess = Progress.prechecking;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.isCheck = true;
        const skipItem = this.getSkipItemList();
        this.checkDetails = [];
        this.getStatus();
        this.updateProgressMotion();
        const params: PreCheckParams = {
            checkType: 'EntireFastUpgrade',
            checkCMD: checkMode,
            skipItem,
        };

        return new Promise((resolve) => {
            // 下发预检查命令  HotpatchPrecheck
            this.http.put(this.http.makeUrl('upgrade.check'), params).subscribe((res) => {
                if (Number(res.result.code) === 0) {
                    this.createPrecheckPolling();
                    this.show();
                    resolve(this.upgradeService.setPrecheckStatus('upgrade'));
                }
            }, () => {
                this.btnEnabled = true;
                this.currentProcess = Progress.precheckFailed;
            });
        });
    }

    // 执行补丁包安装/回滚
    onUpgrade(upgradeType: UpgradeTypeE): void {
        if (upgradeType === UpgradeTypeE.Start) {
            this.currentProcess = Progress.upgrading;
        } else if (upgradeType === UpgradeTypeE.Rollback) {
            // 升级失败,点击回滚按钮,开启超时检测例外
            this.authService.enableTimeoutExcept();
            this.currentProcess = Progress.upgrading;
        }
        switch (upgradeType) {
            case UpgradeTypeE.Upgrade:
                this.currentProcess = Progress.upgrading;
                break;
            case UpgradeTypeE.Retry:
                this.currentProcess = Progress.upgrading;
                break;
            case UpgradeTypeE.Rollback:
                this.currentProcess = Progress.rollBacking; break;
            default:
                break;
        }
        const rest = this.http.makeUrl(upgradeType === UpgradeTypeE.Rollback ? 'upgrade.executeRollback' : 'upgrade.executeUpgrade');
        const activeType = CmoUpdSysModeE.FastUpgrade;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.updateProgressMotion();
        this.http.put(rest, { CMO_EXE_UPD_ACTIVETYPE: activeType, CMO_EXC_ERROR_INFO: upgradeType })
            .subscribe((res) => {
                if (Number(res.result.code) === 0) {
                    // 升级轮训
                    this.isCheck = false;
                    this.checkDetails = [];
                    // 升级标志，用于重入后判断是否执行过升级/回滚
                    this.localStorageService.set('systemUpgrade', true);
                    this.createUpgradePolling();
                }
            }, () => {
                this.checkDetails = [];
                this.getStatus();
                this.btnEnabled = true;
                switch (upgradeType) {
                    case UpgradeTypeE.Upgrade:
                        this.currentProcess = Progress.upgradePause;
                        break;
                    case UpgradeTypeE.Retry:
                        this.currentProcess = Progress.upgradePause;
                        break;
                    case UpgradeTypeE.Rollback:
                        this.currentProcess = Progress.rollBackFailed;
                        break;
                    default:
                        break;
                }
            });
    }

    // 升级后检查
    onVerify(): void {
        this.currentProcess = Progress.verifying;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        this.updateProgressMotion();
        // 下发预检查命令  HotpatchVerify
        this.http.put(this.http.makeUrl('upgrade.check'), { checkType: 'FastPostUpgrade', checkCMD: 0 })
            .pipe(finalize(() => {
                this.isCheck = true;
                this.checkDetails = [];
                this.getStatus();
            }))
            .subscribe(() => {
                this.createVerfiyPolling();
            }, () => {
                // 执行失败置为验证失败
                this.btnEnabled = true;
                this.currentProcess = Progress.verifyFailed;
            });
    }

    // 开启预检查轮询
    createPrecheckPolling(): void {
        // 不重复创建轮询
        if (this.checkResultPolling) {
            return;
        }

        this.extraPolling = false;
        this.currentProcess = Progress.prechecking;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const restJob: RestJob[] = [{
            rest: this.http.makeUrl('upgrade.checkResult'), method: 'GET',
        }, {
            rest: this.http.makeUrl('upgrade.checkProgress'), method: 'GET',
        }];
        this.checkResultPolling = this.poll.addCombo(restJob, 20);
        this.checkResultPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            res[0].data.forEach((item) => {
                const sameIndex = this.checkDetails.findIndex((checkDetailItem) => checkDetailItem.checkItem === item.checkItem);
                if (sameIndex > -1) {
                    // 保持页面上详情展开状态
                    item.showDetail = Boolean(this.checkDetails[sameIndex].showDetail);
                    // 已忽略项保持忽略状态
                    if (this.str.isCheckResultFailed(Number(item.checkResult)) &&
                        Number(this.checkDetails[sameIndex].checkResult) === CheckResult.Ignore) {
                        item.checkResult = CheckResult.Ignore;
                        item.skipByUser = true;
                    }
                }
                // 将换行符\n替换为<br>
                if (item.method) {
                    item.method = item.method.replace(/\\n/g, '<br>');
                }
                if (item.criterion) {
                    item.criterion = item.criterion.replace(/\\n/g, '<br>');
                }
                if (item.suggestion) {
                    item.suggestion = item.suggestion.replace(/\\n/g, '<br>');
                }
                if (typeof item.nodeInfo === 'object') {
                    item.nodeInfoArr = Object.keys(item.nodeInfo).map((key) => ({
                        node: key,
                        // 适配升级组接口更新，将换行符\n替换为<br>
                        info: (item.nodeInfo[key] as string).replace(/\\n/g, '<br>'),
                    }));
                }
            });

            // 排序
            this.checkDetails = this.service.checkDetailsSort(res[0].data);

            // 每次轮询更新一下详情数据
            this.updateDetailData();
            this.getStatus();
            this.upgradeStatus = res[1].data;
            const checkStatus = Number(res[1].data.checkStatus);
            if (checkStatus === CheckStatusE.Done || checkStatus === CheckStatusE.Failed) {
                const detailsAllDone = this.service.ifAllDone(this.checkDetails, 'checkResult', DoneStatus);
                // 检查结束但详情并没有全部完成，额外走一次轮询后再停止
                if (!detailsAllDone && !this.extraPolling) {
                    this.extraPolling = true;
                    return;
                }
                this.poll.remove(this.checkResultPolling);
                this.checkResultPolling = null;
                // 检查当前是否所有进程都成功，是否含有失败项
                if (checkStatus === CheckStatusE.Failed || this.checkDetails.some((value) => this.str.isCheckResultFailed(Number(value.checkResult)))) {
                    this.btnEnabled = true;
                    this.currentProcess = Progress.precheckFailed;
                } else {
                    // 若全部成功则直接执行升级
                    this.currentProcess = Progress.precheckSuc;
                    setTimeout(() => this.onUpgrade(UpgradeTypeE.Start), 3000);
                }
            }
        });
    }

    /**
     * 展示检查详情
     *
     * @param checkItem 检查项字符串
     */
    onShowCheckDetails(checkItem: string): void {
        const checkDetailData = this.service.getCheckDetailData(checkItem, this.checkDetails);

        if (!checkDetailData) {
            return;
        }

        this.checkDetailDataKey = checkItem;
        this.noClosePanel = true;
        if (!this.detailComponent) {
            this.detailComponent = this.lazyLoad.appendComponent(DetailCheckResultUpgradeComponent, {
                onClose: () => {
                    this.detailComponent = null;
                    this.checkDetailDataKey = undefined;
                    this.noClosePanel = false;
                },
                data: checkDetailData,
            });
        } else {
            // 阻止当前侧滑框被关闭
            if (this.detailComponent.instance.slideWindow) {
                this.detailComponent.instance.slideWindow.isPreventClickHide = true;
            }
        }

        // 初始化触发一次data更新事件，方便详情页处理data更新逻辑
        this.detailComponent.instance.data = checkDetailData;
        this.detailComponent.instance.onDataUpdate(checkDetailData, false);
    }

    onShowUpgradeDetails(index: number): void {
        this.checkDetails[index].showDetail = !this.checkDetails[index].showDetail;
    }

    onSkipCheckDetails(index: number): void {
        this.checkDetails[index].checkResult = CheckResult.Ignore;
        this.checkDetails[index].skipByUser = true;
        this.getStatus();
        // 预检查失败状态忽略了所有失败项，则设为预检查成功，并自动开始升级
        if (this.currentProcess === Progress.precheckFailed && this.count.fail === 0) {
            this.currentProcess = Progress.precheckSuc;
            setTimeout(() => this.onUpgrade(UpgradeTypeE.Start), 3000);
        }
    }

    // 开启升级包安装/回滚轮询
    createUpgradePolling(): void {
        // 不重复创建轮询
        if (this.upgradeProgressPolling) {
            return;
        }

        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const restJob: RestJob[] = [{
            // 查询升级包安装/回滚进度
            rest: this.http.makeUrl('upgrade.upgradeProgress').setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.SystemUpgrade, CMO_QUERY_FROM: CmoQueryFrom.DM }),
            method: 'GET',
        }, {
            // 查询升级包安装/回滚详情
            rest: this.http.makeUrl('upgrade.upgradeDetail').setParams({ CMO_QUERY_TYPE: CmoQueryTypeE.SystemUpgrade }),
            method: 'GET',
        }];
        this.upgradeProgressPolling = this.poll.addCombo(restJob, 10);
        this.upgradeProgressPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            this.checkDetails = [];

            res[1].data
                // 同名项只保留后面的
                .filter((originItem, index, arr) => {
                    for (let i = index + 1; i < arr.length; i++) {
                        if (originItem.CMO_STEP_NAME === arr[i].CMO_STEP_NAME &&
                            originItem.CMO_DETAIL_NODE_ID === arr[i].CMO_DETAIL_NODE_ID) {
                            return false;
                        }
                    }
                    return true;
                })
                .forEach((item, i) => {
                    this.checkDetails[i] = {
                        checkResult: item.CMO_DETAIL_ATOM_STATE,
                        checkItem: item.CMO_DETAIL_NODE_ID + this.i18n.colon + item.CMO_STEP_NAME,
                        showDetail: false,
                        nodeInfo: item.CMO_DETAIL_ATOM_RECORD === '' ? '--' : item.CMO_DETAIL_ATOM_RECORD,
                    };
                });

            // 重新排序
            this.checkDetails = this.service.checkDetailsSort(this.checkDetails);

            this.getStatus();

            // 获取升级进度值(进度可能不准确，不能用于判断)
            this.upgradeStatus.checkProgress = Number(res[0].data.CMO_UPD_SYS_STEP_STEP_PERCENT);
            // 获取升级状态
            this.upgradeStatus.status = Number(res[0].data.CMO_UPD_SYS_STEP_TASK_STATUS);

            // 返了“升级失败”，直接退出升级流程
            if (this.upgradeStatus.status === CmoUpdSysStepTaskStatusE.UpgradeFail) {
                // 清除系统升级标志
                this.localStorageService.remove('systemUpgrade');
                // 停止对升级状态的轮询
                this.poll.remove(this.upgradeProgressPolling);
                this.upgradeProgressPolling = null;
                // 关闭超时检测例外
                this.authService.disableTimeoutExcept();
                this.currentProcess = undefined;
                this.iconVisible = false;
                this.visible = false;
            }

            if (this.currentProcess === Progress.upgrading) {
                // 如果不是Upgrading(10)，停止轮询
                if (this.upgradeStatus.status !== CmoUpdSysStepTaskStatusE.Upgrading) {
                    this.localStorageService.remove('systemUpgrade');
                    // 如果不是成功，则标记为失败
                    if (this.upgradeStatus.status !== CmoUpdSysStepTaskStatusE.UpgradeSuc) {
                        this.btnEnabled = true;
                        this.currentProcess = Progress.upgradePause;
                        // 升级失败,关闭超时检测例外
                        this.authService.disableTimeoutExcept();
                    } else {
                        // 若成功则直接将进度设置为100%
                        this.upgradeStatus.checkProgress = 100;
                        this.currentProcess = Progress.upgradeSuc;
                        // 延迟3秒执行升级后校验
                        setTimeout(() => {
                            this.onVerify();
                        }, 3000);
                    }

                    // 停止对升级状态的轮询
                    this.poll.remove(this.upgradeProgressPolling);
                    this.upgradeProgressPolling = null;
                }
            } else if (this.currentProcess === Progress.rollBacking) {
                // 如果不是Upgrading(13)，停止轮询
                if (this.upgradeStatus.status !== CmoUpdSysStepTaskStatusE.Rollbacking) {
                    this.localStorageService.remove('systemUpgrade');
                    // 如果不是成功，则标记为失败
                    if (this.upgradeStatus.status !== CmoUpdSysStepTaskStatusE.RollbackSuc) {
                        this.btnEnabled = true;
                        this.currentProcess = Progress.rollBackFailed;
                    } else {
                        // 若成功则直接将进度设置为100%
                        this.upgradeStatus.checkProgress = 100;
                        this.currentProcess = Progress.rollBackSuc;
                    }
                    // 停止对升级状态的轮询
                    this.poll.remove(this.upgradeProgressPolling);
                    this.upgradeProgressPolling = null;
                }
            } else if (this.currentProcess === Progress.upgradePause || this.currentProcess === Progress.rollBackFailed) {
                this.poll.remove(this.upgradeProgressPolling);
                // 升级/回滚暂停重入,关闭超时检测例外
                this.authService.disableTimeoutExcept();
                this.upgradeProgressPolling = null;
                this.show();
            }
        });
    }

    // 开启验证轮训
    createVerfiyPolling(): void {
        // 不重复创建轮询
        if (this.checkResultPolling) {
            return;
        }

        this.extraPolling = false;
        this.currentProcess = Progress.verifying;
        this.upgradeStatus.checkProgress = 0;
        this.btnEnabled = false;
        const restJob: RestJob[] = [{
            rest: this.http.makeUrl('upgrade.checkResult'), method: 'GET',
        }, {
            rest: this.http.makeUrl('upgrade.checkProgress'), method: 'GET',
        }];
        this.checkResultPolling = this.poll.addCombo(restJob, 20);
        this.checkResultPolling.subscribe((res) => {
            if (!this.authService.getTimeoutExcept()) {
                // 开启超时检测例外
                this.authService.enableTimeoutExcept();
            }
            let sCount = 0;
            let fCount = 0;
            res[0].data.forEach((item) => {
                const sameIndex = this.checkDetails.findIndex((checkDetailItem) => checkDetailItem.checkItem === item.checkItem);
                // 保持页面详情展开状态
                if (sameIndex > -1 && this.checkDetails[sameIndex].showDetail) {
                    item.showDetail = true;
                } else {
                    item.showDetail = false;
                }
                // 将换行符\n替换为<br>
                if (item.method) {
                    item.method = item.method.replace(/\\n/g, '<br>');
                }
                if (item.criterion) {
                    item.criterion = item.criterion.replace(/\\n/g, '<br>');
                }
                if (item.suggestion) {
                    item.suggestion = item.suggestion.replace(/\\n/g, '<br>');
                }
                if (typeof item.nodeInfo === 'object') {
                    item.nodeInfoArr = Object.keys(item.nodeInfo).map((key) => ({
                        node: key,
                        // 适配升级组接口更新，将换行符\n替换为<br>
                        info: (item.nodeInfo[key] as string).replace(/\\n/g, '<br>'),
                    }));
                }
                if (Number(item.checkResult) === CheckResult.Suc) {
                    sCount = sCount + 1;
                }
                if (Number(item.checkResult) === CheckResult.Fail) {
                    fCount = fCount + 1;
                }
            });

            // 排序
            this.checkDetails = this.service.checkDetailsSort(res[0].data);

            // 每次轮询更新一下详情数据
            this.updateDetailData();

            this.getStatus();
            this.upgradeStatus = res[1].data;
            const checkStatus = Number(res[1].data.checkStatus);
            if (checkStatus === CheckStatusE.Done || checkStatus === CheckStatusE.Failed) {
                const detailsAllDone = this.service.ifAllDone(this.checkDetails, 'checkResult', DoneStatus);
                // 检查结束但详情并没有全部完成，额外走一次轮询后再停止
                if (!detailsAllDone && !this.extraPolling) {
                    this.extraPolling = true;
                    return;
                }
                this.poll.remove(this.checkResultPolling);
                this.checkResultPolling = null;
                // 检查当前是否所有进程都成功
                if (checkStatus === CheckStatusE.Failed ||
                    this.checkDetails.find((value) => this.str.isCheckResultFailed(Number(value.checkResult)))) {
                    this.btnEnabled = true;
                    this.currentProcess = Progress.verifyFailed;
                } else {
                    this.currentProcess = Progress.verifySuc;
                }
            }
        });
    }

    showResultPage(current: Progress): boolean {
        const finishBtnArr = [Progress.rollBackSuc, Progress.verifySuc, Progress.verifyFailed, Progress.upgradeFailed];
        return finishBtnArr.includes(current);
    }

    sucPageInfo = (current: Progress): string => {
        switch (current) {
            case Progress.verifySuc: return this.i18n.verifySuc;
            case Progress.verifyFailed: return this.i18n.verifyFailed;
            case Progress.rollBackSuc: return this.i18n.rollBackSuc;
            default:
                return '';
        }
    };

    getStatusIconSvg = (current: Progress): string => {
        if ([Progress.precheckFailed, Progress.upgradeFailed, Progress.rollBackFailed, Progress.verifyFailed,
        Progress.upgradePause].includes(current)) {
            return StatusIcon.Fail;
        }
        if ([Progress.verifySuc, Progress.rollBackSuc].includes(current)) {
            return StatusIcon.Suc;
        }
        return StatusIcon.Upgrading;
    };

    /**
     * 更新详情数据
     */
    updateDetailData(): void {
        if (!this.detailComponent || isUndefined(this.checkDetailDataKey)) {
            return;
        }
        const checkDetailData = this.service.getCheckDetailData(this.checkDetailDataKey, this.checkDetails);
        // 初始化触发一次data更新事件，方便详情页处理data更新逻辑
        this.detailComponent.instance.data = checkDetailData;
        this.detailComponent.instance.onDataUpdate(checkDetailData, false);
    }

    /**
     * 升级面板关闭前的检查，当侧划框被打开时，不允许关闭
     */
    beforePanelHide = (): boolean => !this.noClosePanel;

    // 取消
    onCancel(): void {
        if (this.btnEnabled === true) {
            // 如果按钮激活，则关闭窗口
            this.iconVisible = false;
            this.visible = false;
            this.currentProcess = undefined;
            // 退出升级流程
            this.mainUpgradeService.inUpgradeProcess = false;
            // 关闭超时检测例外
            this.authService.disableTimeoutExcept();
        }
    }

    // 重试
    onRetry(): void {
        if (this.currentProcess === Progress.precheckFailed || this.currentProcess === Progress.precheckSuc) {
            this.precheck(true);
            this.btnEnabled = false;
        } else if (this.currentProcess === Progress.upgradePause) {
            // 升级暂停,点击重试按钮,开启超时检测例外
            this.authService.enableTimeoutExcept();
            this.onUpgrade(UpgradeTypeE.Retry);
            this.btnEnabled = false;
        }
    }

    showRetry(current: Progress): boolean {
        return [Progress.precheckFailed, Progress.upgradePause].includes(current);
    }

    onClose(): void {
        this.iconVisible = false;
        this.visible = false;
        window.location.reload();
    }

    ngOnDestroy(): void {
        // 关闭超时检测例外
        this.authService.disableTimeoutExcept();
        this.sub$?.unsubscribe();

        if (this.inUpgradeProcessSub$) {
            this.inUpgradeProcessSub$.unsubscribe();
        }

        if (this.upgradeProgressPolling) {
            this.upgradeProgressPolling.unsubscribe();
        }
    }
}
